import { useEffect, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Database, Download, Trash2, RefreshCw, Loader2,
  ChevronLeft, ChevronRight, Eye, Plus, FileArchive, ClipboardList, User,
  Search
} from "lucide-react";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  approve: "bg-violet-100 text-violet-700",
  reject: "bg-amber-100 text-amber-700",
  login: "bg-slate-100 text-slate-700",
  login_failed: "bg-red-100 text-red-700",
  logout: "bg-slate-100 text-slate-600",
  permissions_update: "bg-purple-100 text-purple-700",
  export: "bg-cyan-100 text-cyan-700",
  backup_create: "bg-indigo-100 text-indigo-700",
};

const ACTION_LABELS = {
  create: "Criou", update: "Atualizou", delete: "Excluiu",
  approve: "Aprovou", reject: "Rejeitou",
  login: "Login OK", login_failed: "Login Falhou", logout: "Logout",
  permissions_update: "Permissões",
  export: "Exportou", backup_create: "Backup",
};

const ENTITY_LABELS = {
  cliente_fornecedor: "Cliente/Fornecedor", funcionario: "Funcionário",
  cargo: "Cargo", orcamento: "Orçamento", contrato: "Contrato",
  crm_lead: "Lead CRM", backup: "Backup", system: "Sistema",
  user: "Usuário", auth: "Autenticação",
};

function MaskedJson({ data }) {
  if (!data) return <span className="text-slate-400 italic">vazio</span>;
  return (
    <pre className="text-[10px] bg-slate-50 p-2 rounded max-h-40 overflow-auto whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [filters, setFilters] = useState({ entity_type: "", action: "", days: 30 });
  const [detail, setDetail] = useState(null);
  const limit = 20;
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit, days: filters.days });
      if (filters.entity_type) params.append("entity_type", filters.entity_type);
      if (filters.action) params.append("action", filters.action);
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/audit-logs?${params}`, { headers }),
        axios.get(`${API}/audit-logs/stats?days=${filters.days}`, { headers }),
      ]);
      setLogs(logsRes.data.items || []);
      setTotal(logsRes.data.total || 0);
      setStats(statsRes.data);
    } catch { toast.error("Erro ao carregar audit logs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, [skip, filters.entity_type, filters.action, filters.days]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500">Total ({filters.days}d)</p>
              <p className="text-2xl font-bold text-[#0A1628]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500 mb-1">Por Acao</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(stats.por_acao).slice(0, 4).map(([k, v]) => (
                  <Badge key={k} className={`${ACTION_COLORS[k] || "bg-slate-100"} text-[10px]`}>{ACTION_LABELS[k] || k}: {v}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500 mb-1">Top Entidades</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(stats.por_entidade).slice(0, 3).map(([k, v]) => (
                  <span key={k} className="text-[10px] text-slate-600">{ENTITY_LABELS[k] || k}: {v}</span>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500 mb-1">Top Usuarios</p>
              {Object.entries(stats.por_usuario_top10 || {}).slice(0, 3).map(([k, v]) => (
                <p key={k} className="text-[10px] text-slate-600 truncate">{k}: <b>{v}</b></p>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <NativeSelect value={filters.entity_type} onValueChange={v => { setFilters(f => ({ ...f, entity_type: v })); setSkip(0); }} options={[
          { value: "", label: "Toda entidade" },
          { value: "auth", label: "Autenticação" },
          { value: "user", label: "Usuário" },
          { value: "cliente_fornecedor", label: "Cliente/Fornecedor" },
          { value: "funcionario", label: "Funcionário" },
          { value: "crm_lead", label: "Lead CRM" },
          { value: "orcamento", label: "Orçamento" },
          { value: "contrato", label: "Contrato" },
          { value: "backup", label: "Backup" },
          { value: "system", label: "Sistema" },
        ]} className="h-9 w-44 text-sm" />
        <NativeSelect value={filters.action} onValueChange={v => { setFilters(f => ({ ...f, action: v })); setSkip(0); }} options={[
          { value: "", label: "Toda ação" },
          { value: "login", label: "Login OK" },
          { value: "login_failed", label: "Login Falhou" },
          { value: "logout", label: "Logout" },
          { value: "create", label: "Criar" },
          { value: "update", label: "Atualizar" },
          { value: "delete", label: "Excluir" },
          { value: "permissions_update", label: "Permissões" },
          { value: "approve", label: "Aprovar" },
          { value: "export", label: "Exportar" },
        ]} className="h-9 w-36 text-sm" />
        <NativeSelect value={String(filters.days)} onValueChange={v => { setFilters(f => ({ ...f, days: parseInt(v) })); setSkip(0); }} options={[
          { value: "7", label: "7 dias" },
          { value: "30", label: "30 dias" },
          { value: "90", label: "90 dias" },
          { value: "365", label: "1 ano" },
        ]} className="h-9 w-24 text-sm" />
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setFilters(f => ({ ...f, entity_type: "auth", action: "" })); setSkip(0); }}
          data-testid="filter-historico-acessos"
        >
          <Shield className="h-3 w-3 mr-1" />Histórico de Acessos
        </Button>
        <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="audit-refresh-btn"><RefreshCw className="h-3 w-3 mr-1" />Atualizar</Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A1628]" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">Nenhum log encontrado</p>
          ) : (
            <div className="divide-y">
              {logs.map(log => (
                <div key={log.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail(log)} data-testid={`audit-row-${log.id}`}>
                  <div className="flex items-center gap-3">
                    <Badge className={`${ACTION_COLORS[log.action] || "bg-slate-100"} text-[10px] shrink-0`}>{ACTION_LABELS[log.action] || log.action}</Badge>
                    <span className="text-sm text-slate-600 truncate flex-1">
                      <b>{log.user_name || "?"}</b> em <span className="font-mono">{ENTITY_LABELS[log.entity_type] || log.entity_type}</span>
                      {log.detail && <span className="text-slate-500"> — {log.detail}</span>}
                    </span>
                    <span className="text-xs text-slate-400 shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleString("pt-BR") : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Pagina {currentPage} de {totalPages} ({total} registros)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}><ChevronLeft className="h-3 w-3" /></Button>
            <Button variant="outline" size="sm" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}><ChevronRight className="h-3 w-3" /></Button>
          </div>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0A1628] flex items-center gap-2">
                  <Badge className={ACTION_COLORS[detail.action] || "bg-slate-100"}>{ACTION_LABELS[detail.action] || detail.action}</Badge>
                  {ENTITY_LABELS[detail.entity_type] || detail.entity_type}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="text-xs space-y-0.5 mt-2">
                    <p><b>Usuario:</b> {detail.user_name} ({detail.user_role})</p>
                    <p><b>Quando:</b> {new Date(detail.timestamp).toLocaleString("pt-BR")}</p>
                    {detail.entity_id && <p><b>Entidade ID:</b> <span className="font-mono">{detail.entity_id}</span></p>}
                    {detail.detail && <p><b>Detalhe:</b> {detail.detail}</p>}
                  </div>
                </DialogDescription>
              </DialogHeader>
              <Separator />
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-1">Antes</p>
                  <MaskedJson data={detail.before} />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 mb-1">Depois</p>
                  <MaskedJson data={detail.after} />
                </div>
                <p className="text-[10px] text-slate-400 italic">Campos PII (documento, email, pix, etc) sao mascarados nos logs por seguranca.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BackupTab() {
  const [backups, setBackups] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/backup/list`, { headers });
      setBackups(res.data.items || []);
      setRetention(res.data.retention_days || 30);
    } catch { toast.error("Erro ao listar backups"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/backup/create`, {}, { headers });
      toast.success(`Backup criado: ${res.data.filename} (${res.data.size_mb} MB)`);
      fetchAll();
    } catch { toast.error("Erro ao criar backup"); }
    finally { setCreating(false); }
  };

  const downloadBackup = (filename) => {
    const url = `${BACKEND_URL}/api/admin/backup/download/${filename}`;
    fetch(url, { headers }).then(r => r.blob()).then(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => toast.error("Erro ao baixar"));
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Excluir backup ${filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/backup/${filename}`, { headers });
      toast.success("Backup removido");
      fetchAll();
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm bg-blue-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <FileArchive className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[#0A1628]">Backup Local Completo</p>
            <p className="text-xs text-slate-600 mt-1">
              Cada backup inclui todas as coleções do MongoDB compactadas em JSON gzip.
              Retenção automática: <b>{retention} dias</b>. PII permanece encriptado no backup.
            </p>
          </div>
          <Button onClick={createBackup} disabled={creating} className="bg-[#0A1628]" size="sm" data-testid="btn-criar-backup">
            <Plus className="h-4 w-4 mr-1" />{creating ? "Criando..." : "Criar backup agora"}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0A1628]" /></div>
          ) : backups.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">Nenhum backup criado ainda</p>
          ) : (
            <div className="divide-y">
              {backups.map(b => (
                <div key={b.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`backup-row-${b.id}`}>
                  <FileArchive className="h-5 w-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono text-[#0A1628] truncate">{b.filename}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(b.created_at).toLocaleString("pt-BR")} · {b.size_mb} MB · {b.total_documents} docs · {b.created_by}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => downloadBackup(b.filename)} title="Baixar">
                    <Download className="h-4 w-4 text-[#0A1628]" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteBackup(b.filename)} title="Excluir">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminSecurityPage() {
  const [tab, setTab] = useState("audit");

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-security-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2">
            <Shield className="h-6 w-6" />Segurança & Auditoria
          </h1>
          <p className="text-sm text-slate-500 mt-1">Logs de auditoria de todas as alterações e backups do sistema.</p>
        </div>

        <div className="flex gap-2 border-b">
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "audit" ? "border-[#0A1628] text-[#0A1628]" : "border-transparent text-slate-500 hover:text-[#0A1628]"}`}
            onClick={() => setTab("audit")}
            data-testid="tab-audit"
          >
            <ClipboardList className="h-4 w-4 inline mr-1" />Audit Trail
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === "backup" ? "border-[#0A1628] text-[#0A1628]" : "border-transparent text-slate-500 hover:text-[#0A1628]"}`}
            onClick={() => setTab("backup")}
            data-testid="tab-backup"
          >
            <Database className="h-4 w-4 inline mr-1" />Backups
          </button>
        </div>

        {tab === "audit" ? <AuditTab /> : <BackupTab />}
      </div>
    </MainLayout>
  );
}
